/**
 * InvoicePdf — Phase 98au (09/07/2026)
 *
 * Rendu PDF facture aligné 1:1 sur le rendu du devis (`CateringQuotePdf`) —
 * la facture reprend TEL QUEL le devis avec 3 ajouts :
 *   • Titre FACTURE (noir) + N° facture
 *   • Bloc « Références » : N° devis source, date devis, événement/titre
 *   • Bloc « Conditions de paiement » : délai (défaut 10 jours) + IBAN
 *   • QR-Bill Swiss (PNG rasterisé, passé via prop `qrPngDataUrl`)
 *
 * Règle métier prix (validée par le client) :
 *   - Défaut B2B / revendeurs : prix HT + TVA + Total TTC (détail classique)
 *   - Particuliers (tarif boutique/distributeurs) : prix TTC, la TVA figure
 *     entre parenthèses après le Total TTC — « (dont TVA X.XX CHF) »
 */
import React from "react";
import { Document, Page, View, Text, Image, StyleSheet } from "@react-pdf/renderer";
import { COMPANY_HEADER } from "../lib/company";

const fmtCHF = (n) => `${(Number(n) || 0).toFixed(2)}`;
const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("fr-CH", { day: "2-digit", month: "long", year: "numeric" });
  } catch { return iso; }
};

const styles = StyleSheet.create({
  page: { padding: 36, fontFamily: "Helvetica", fontSize: 9, color: "#1A1C1A" },

  // Header row : logo + créditeur (gauche) + titre FACTURE (droite)
  headerRow: { flexDirection: "row", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 14 },
  senderBlock: { flexDirection: "row", alignItems: "flex-start", gap: 10, maxWidth: "58%" },
  senderLogo: { width: 55, height: 55 },
  senderInfo: { flexDirection: "column" },
  senderName: { fontSize: 15, fontFamily: "Helvetica-Bold", marginBottom: 4, letterSpacing: 0.6 },
  senderMeta: { fontSize: 8, color: "#575A57", lineHeight: 1.4 },

  titleRight: { flexDirection: "column", alignItems: "flex-end", maxWidth: "40%" },
  titleMain: { fontSize: 22, fontFamily: "Helvetica-Bold", letterSpacing: 1.2, color: "#1A1C1A" },
  titleSub: { fontSize: 8.5, color: "#575A57", marginTop: 1, marginBottom: 6 },
  titleRef: { fontSize: 11, fontFamily: "Helvetica-Bold", marginTop: 2 },
  titleDate: { fontSize: 8.5, color: "#575A57", marginTop: 1 },

  // Phase 101cl (18/02/2026) — Adresse débiteur en position fenêtre B5/C5 (SN 351).
  // Standard postal suisse pour enveloppes à fenêtre droite : top 45mm, left 120mm,
  // largeur 85mm × hauteur 40mm. Idem pour BL, facture, devis, courrier.
  // Note : react-pdf accepte les valeurs en mm sous forme de string.
  debtorWindow: {
    position: "absolute",
    top: "48mm",
    left: "115mm",
    width: "85mm",
    height: "45mm",
    padding: 4,
  },
  debtorLabel: {
    fontSize: 6, color: "#A3A6A3", textTransform: "uppercase",
    letterSpacing: 0.5, marginBottom: 2,
  },
  debtorName: { fontSize: 10, fontFamily: "Helvetica-Bold", marginBottom: 1 },
  debtorLine: { fontSize: 9.5, color: "#1A1C1A", lineHeight: 1.35 },

  // Dual-cards ÉMETTEUR + CLIENT sous le header (deprecated, gardé pour compat)
  partiesRow: { flexDirection: "row", gap: 12, marginBottom: 12 },
  partyCard: { flex: 1, padding: 10, backgroundColor: "#F4F4F0" },
  partyLabel: { fontSize: 7, color: "#A3A6A3", textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 4 },
  partyName: { fontSize: 11, fontFamily: "Helvetica-Bold", marginBottom: 3 },
  partyLine: { fontSize: 9, color: "#1A1C1A", lineHeight: 1.35 },

  // Références (devis source) + Conditions de paiement (deux blocs côte à côte)
  refsRow: { flexDirection: "row", gap: 12, marginBottom: 12 },
  refBox: { flex: 1, padding: 8, borderLeft: "3pt solid #C1A063", backgroundColor: "#FAFAF7" },
  refBoxPay: { flex: 1, padding: 8, borderLeft: "3pt solid #1F4E3D", backgroundColor: "#FAFAF7" },
  refLabel: { fontSize: 7, color: "#A3A6A3", textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 3 },
  refLine: { fontSize: 9, color: "#1A1C1A", lineHeight: 1.4 },
  refStrong: { fontSize: 9.5, fontFamily: "Helvetica-Bold", color: "#1A1C1A" },

  // Événement (bloc doré)
  eventBox: { marginBottom: 12, padding: 10, borderWidth: 1, borderColor: "#C1A063", borderStyle: "solid", backgroundColor: "#FBF4E9" },
  eventTitle: { fontSize: 9, fontFamily: "Helvetica-Bold", color: "#8C6B2F", textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 4 },
  eventGrid: { flexDirection: "row", flexWrap: "wrap", gap: 12 },
  eventItem: { flexDirection: "column", minWidth: 100 },
  eventLabel: { fontSize: 7, color: "#A3A6A3", textTransform: "uppercase", letterSpacing: 0.4 },
  eventValue: { fontSize: 10, color: "#1A1C1A", fontFamily: "Helvetica-Bold", marginTop: 1 },

  // Table
  table: { borderTop: "1pt solid #E5E5E0", borderLeft: "1pt solid #E5E5E0", borderRight: "1pt solid #E5E5E0", marginBottom: 6 },
  th: { flexDirection: "row", backgroundColor: "#F4F4F0", padding: 6, borderBottom: "1pt solid #E5E5E0" },
  thCell: { fontFamily: "Helvetica-Bold", fontSize: 8, color: "#575A57", textTransform: "uppercase", letterSpacing: 0.5 },
  tr: { flexDirection: "row", padding: 6, borderBottom: "1pt solid #E5E5E0", alignItems: "flex-start" },
  td: { fontSize: 9 },

  // Totaux
  totals: { alignSelf: "flex-end", width: "55%", marginTop: 6 },
  totalRow: { flexDirection: "row", justifyContent: "space-between", paddingVertical: 2 },
  totalLabel: { fontSize: 9, color: "#575A57" },
  totalValue: { fontSize: 9, fontFamily: "Helvetica-Bold" },
  totalGrand: { flexDirection: "row", justifyContent: "space-between", alignItems: "flex-start", paddingTop: 6, marginTop: 4, borderTop: "1.5pt solid #1A1C1A" },
  paidRow: { marginTop: 4, paddingTop: 4, borderTop: "0.5pt solid #E5E5E0" },
  paidText: { fontSize: 9, color: "#575A57" },

  // QR-Bill
  qrBillBox: { marginTop: 12, borderTop: "0.5pt dashed #A3A6A3", paddingTop: 4 },
  qrBillImage: { width: "100%", height: 105 * 2.835 },
  qrBillMissing: {
    fontSize: 9, color: "#8C6B2F",
    padding: 12, textAlign: "center",
    backgroundColor: "#FBF4E9",
  },
  // Phase 101bz — page dédiée QR-Bill (page 2)
  qrPageHint: {
    fontSize: 9, color: "#575A57",
    marginBottom: 12, textAlign: "center",
    borderBottom: "0.5pt dashed #A3A6A3", paddingBottom: 8,
  },
  qrBillBoxPage2: { marginTop: 0, paddingTop: 0 },

  // Footer
  footer: { position: "absolute", bottom: 24, left: 36, right: 36, fontSize: 7, color: "#A3A6A3", textAlign: "center", borderTop: "0.5pt solid #E5E5E0", paddingTop: 6 },
});

// Phase 101bz (18/02/2026) — Arrondi commercial suisse (« Rappenrundung »)
// au multiple de 0.05 CHF le plus proche, avec règle "half up".
const swissRound = (amount) => Math.round(Number(amount || 0) * 20) / 20;

// Phase 101gd (Feb 2026) — Exporté pour usage QR-Bill (InvoiceDetail.jsx).
// Toutes les factures avec remise ou frais additionnels avaient un QR-Bill au
// SOUS-TOTAL au lieu du TOTAL FINAL. Fix critique : le QR-Bill doit utiliser
// cette fonction, pas `inv.total_ttc` de la DB (qui = sous-total pour ces cas).
export function computeTotals(inv) {
  const lines = inv?.lines || [];
  let subtotal = 0;
  const vatBase = {};
  lines.forEach((ln) => {
    const amount = Number(ln.quantity || 0) * Number(ln.unit_price || 0);
    subtotal += amount;
    const r = Number(ln.vat_rate || 0);
    vatBase[r] = (vatBase[r] || 0) + amount;
  });
  const includeVat = !!inv?.prices_include_vat;
  const discountPct = Number(inv?.discount_percent || 0);
  const discountAmount = discountPct > 0 ? Math.round(subtotal * discountPct) / 100 : 0;
  // Applique la remise proportionnellement à chaque taux de TVA
  const scaledVatBase = {};
  const scale = discountAmount > 0 && subtotal > 0 ? (subtotal - discountAmount) / subtotal : 1;
  Object.entries(vatBase).forEach(([r, base]) => { scaledVatBase[r] = base * scale; });

  // Phase 101ea.6 (Feb 2026) — Frais additionnels du devis traiteur (livraison,
  // service, minimum garanti…). Ajoutés APRÈS la remise (car conservés bruts)
  // et intégrés dans la vatBase pour extraire la bonne TVA par taux.
  const fees = inv?.additional_fees || [];
  let feesTotal = 0;
  fees.forEach((f) => {
    const amount = Number(f.amount || 0);
    if (!amount) return;
    feesTotal += amount;
    const r = Number(f.vat_rate || 0);
    scaledVatBase[r] = (scaledVatBase[r] || 0) + amount;
  });

  let ht, vat, ttc;
  if (includeVat) {
    ttc = subtotal - discountAmount + feesTotal;
    vat = Object.entries(scaledVatBase).reduce((s, [r, base]) => s + (Number(r) ? base - base / (1 + Number(r) / 100) : 0), 0);
    ht = ttc - vat;
  } else {
    ht = subtotal - discountAmount + feesTotal;
    vat = Object.entries(scaledVatBase).reduce((s, [r, base]) => s + base * Number(r) / 100, 0);
    ttc = ht + vat;
  }
  // Phase 101bz — Arrondi commercial suisse sur le TTC final, écart affiché en ligne
  // « Arrondi » pour cohérence comptable (HT + TVA + arrondi = TTC facturé).
  const ttcRaw = Math.round(ttc * 100) / 100;
  const ttcRounded = swissRound(ttcRaw);
  const roundingAdjustment = Math.round((ttcRounded - ttcRaw) * 100) / 100;
  return {
    subtotal,       // avant remise
    discountAmount,
    discountPct,
    feesTotal,      // Phase 101ea.6 — Somme des frais additionnels
    fees,           // Phase 101ea.6 — Liste brute pour affichage détail
    ht: Math.round(ht * 100) / 100,
    vat: Math.round(vat * 100) / 100,
    ttc: ttcRounded,
    ttcRaw,
    roundingAdjustment,
    vatGroups: scaledVatBase,
    includeVat,
  };
}

export function InvoicePdf({ inv, invoice, settings, qrPngDataUrl }) {
  // Rétro-compat : accepte inv (nouveau) ou invoice (ancien nom de prop)
  inv = inv || invoice;
  if (!inv) return null;
  const t = computeTotals(inv);
  const paid = Number(inv?.paid_amount || 0);
  const credit = Number(inv?.credit_applied_amount || 0);
  const remaining = Math.max(0, t.ttc - paid);
  const isCH = !inv.debtor_country || inv.debtor_country === "CH" || inv.debtor_country === "Suisse";
  // Phase 98au — Fallback créditeur : privilégie inv.creditor_*, sinon settings.company_*
  const cAddr = (inv.creditor_address || settings?.company_address || "").split("\n").filter(Boolean);
  const cZip = inv.creditor_postal_code || settings?.company_postal_code || "";
  const cCity = inv.creditor_city || settings?.company_city || "";
  const cPhone = inv.creditor_phone || settings?.company_phone || "";
  const cVat = inv.creditor_vat_number || settings?.company_vat_number || "";
  // Délai de paiement — par défaut « payable à 10 jours »
  const paymentTerms = inv.payment_terms || "Payable à 10 jours net";

  return (
    <Document>
      <Page size="A4" style={styles.page}>

        {/* === EN-TÊTE : Émetteur + Titre FACTURE === */}
        <View style={styles.headerRow}>
          <View style={styles.senderBlock}>
            {settings?.company_logo_url
              ? <Image src={settings.company_logo_url} style={styles.senderLogo} />
              : <Image src={`${typeof window !== "undefined" ? window.location.origin : ""}/logo-maisonthai-pdf.png`} style={styles.senderLogo} />}
            <View style={styles.senderInfo}>
              <Image
                src={`${typeof window !== "undefined" ? window.location.origin : ""}/company-name-sayonara.png`}
                style={{ width: 149, height: 15, marginBottom: 4 }}
              />
              {cAddr.slice(0, 1).map((line, i) => (
                <Text key={i} style={styles.senderMeta}>{line}</Text>
              ))}
              {cZip && cCity && (
                <Text style={styles.senderMeta}>CH-{cZip} {cCity}</Text>
              )}
              {cPhone && <Text style={styles.senderMeta}>Tél {cPhone}</Text>}
              {cVat && <Text style={styles.senderMeta}>TVA : {cVat}</Text>}
            </View>
          </View>

          <View style={styles.titleRight}>
            <Text style={styles.titleMain}>FACTURE</Text>
            <Text style={styles.titleRef}>N° {inv.reference}</Text>
            <Text style={styles.titleDate}>Émise le {fmtDate(inv.issue_date)}</Text>
            {inv.due_date && (
              <Text style={styles.titleDate}>Échéance : {fmtDate(inv.due_date)}</Text>
            )}
          </View>
        </View>

        {/* === Cartes ÉMETTEUR + CLIENT === */}
        {/* === Phase 101cl — Adresse débiteur en position fenêtre B5/C5 (SN 351) ===
            L'adresse est positionnée en absolu (top 48mm, left 115mm) pour être
            visible dans la fenêtre droite d'une enveloppe B5/C5. Le format est
            conforme au standard postal suisse : Société / Contact / Rue / NPA
            Ville (max 4-5 lignes pour tenir dans la fenêtre). */}
        <View style={styles.debtorWindow} data-testid="pdf-debtor-window">
          {inv.debtor_company ? (
            <>
              <Text style={styles.debtorName}>{inv.debtor_company}</Text>
              {inv.debtor_name ? <Text style={styles.debtorLine}>{inv.debtor_name}</Text> : null}
            </>
          ) : (
            <Text style={styles.debtorName}>{inv.debtor_name || "Client"}</Text>
          )}
          {inv.debtor_contact_name ? <Text style={styles.debtorLine}>{inv.debtor_contact_name}</Text> : null}
          {inv.debtor_address ? <Text style={styles.debtorLine}>{inv.debtor_address}</Text> : null}
          {(inv.debtor_postal_code || inv.debtor_city) && (
            <Text style={styles.debtorLine}>{isCH ? "CH-" : ""}{inv.debtor_postal_code} {inv.debtor_city}</Text>
          )}
          {!isCH && inv.debtor_country ? <Text style={styles.debtorLine}>{inv.debtor_country}</Text> : null}
        </View>

        {/* Spacer pour laisser passer la fenêtre absolue (48mm + 45mm = 93mm depuis le haut).
            Le contenu suivant commence donc à environ 100mm — sous la zone fenêtre. */}
        <View style={{ height: "50mm" }} />

        {/* === Références devis + Conditions de paiement === */}
        <View style={styles.refsRow}>
          <View style={styles.refBox}>
            <Text style={styles.refLabel}>Références</Text>
            {inv.source_quote_reference ? (
              <Text style={styles.refLine}>Devis : <Text style={styles.refStrong}>{inv.source_quote_reference}</Text></Text>
            ) : null}
            {inv.source_quote_date ? (
              <Text style={styles.refLine}>Date devis : {fmtDate(inv.source_quote_date)}</Text>
            ) : null}
            {/* Phase 101bt — Références BL (revendeur) : n° BL et date de livraison */}
            {inv.source_bl_reference ? (
              <Text style={styles.refLine}>Bon de livraison : <Text style={styles.refStrong}>{inv.source_bl_reference}</Text></Text>
            ) : null}
            {inv.delivery_date ? (
              <Text style={styles.refLine}>Date de livraison : <Text style={styles.refStrong}>{fmtDate(inv.delivery_date)}</Text></Text>
            ) : null}
            {inv.event_service_type ? (
              <Text style={styles.refLine}>Objet : {inv.event_service_type}</Text>
            ) : (inv.additional_info ? (
              <Text style={styles.refLine}>Objet : {inv.additional_info}</Text>
            ) : null)}
            {/* Phase 101cf (18/02/2026) — Commentaire libre facultatif */}
            {inv.custom_comment ? (
              <Text style={styles.refLine}>Commentaire : <Text style={styles.refStrong}>{inv.custom_comment}</Text></Text>
            ) : null}
            {/* Phase 101gd (Feb 2026) — Import rétroactif : trace de la facture papier remplacée */}
            {inv.legacy_reference ? (
              <Text style={styles.refLine}>
                Remplace la facture papier n° <Text style={styles.refStrong}>{inv.legacy_reference}</Text>
                {inv.legacy_date ? <> du <Text style={styles.refStrong}>{fmtDate(inv.legacy_date)}</Text></> : null}
              </Text>
            ) : null}
          </View>
          <View style={styles.refBoxPay}>
            <Text style={styles.refLabel}>Conditions de paiement</Text>
            <Text style={styles.refStrong}>{paymentTerms}</Text>
            {inv.creditor_iban ? (
              <Text style={styles.refLine}>IBAN : {inv.creditor_iban}</Text>
            ) : null}
            {inv.qr_reference ? (
              <Text style={styles.refLine}>Réf. QR : {inv.qr_reference}</Text>
            ) : null}
          </View>
        </View>

        {/* === Événement (si présent) === */}
        {(inv.event_date || inv.event_location || inv.event_guests_count) && (
          <View style={styles.eventBox}>
            <Text style={styles.eventTitle}>Détails de l&apos;événement</Text>
            <View style={styles.eventGrid}>
              {inv.event_date && (
                <View style={styles.eventItem}>
                  <Text style={styles.eventLabel}>Date</Text>
                  <Text style={styles.eventValue}>{fmtDate(inv.event_date)}</Text>
                </View>
              )}
              {inv.event_location && (
                <View style={styles.eventItem}>
                  <Text style={styles.eventLabel}>Lieu</Text>
                  <Text style={styles.eventValue}>{inv.event_location}</Text>
                </View>
              )}
              {inv.event_guests_count && (
                <View style={styles.eventItem}>
                  <Text style={styles.eventLabel}>Personnes</Text>
                  <Text style={styles.eventValue}>{inv.event_guests_count}</Text>
                </View>
              )}
            </View>
          </View>
        )}

        {/* === Table lignes === */}
        <View style={styles.table}>
          <View style={styles.th}>
            <Text style={[styles.thCell, { flex: 4 }]}>Désignation</Text>
            <Text style={[styles.thCell, { flex: 1.1, textAlign: "right" }]}>Qté / Unité</Text>
            <Text style={[styles.thCell, { flex: 1, textAlign: "right" }]}>PU CHF</Text>
            <Text style={[styles.thCell, { flex: 0.6, textAlign: "right" }]}>TVA</Text>
            <Text style={[styles.thCell, { flex: 1.1, textAlign: "right" }]}>Total CHF</Text>
          </View>
          {(inv.lines || []).map((ln, idx) => {
            const total = (Number(ln.quantity) || 0) * (Number(ln.unit_price) || 0);
            const qtyUnit = `${ln.quantity}${ln.unit || ""}`;
            // Phase 101ea.7 (Feb 2026) — Lignes offertes (unit_price=0) : afficher
            // « Offert » au lieu de « 0.00 CHF » pour être cohérent avec le PDF devis.
            const isOffered = Number(ln.unit_price || 0) === 0 && (ln.description || "").trim();
            return (
              <View key={ln.id || idx} style={styles.tr}>
                <Text style={[styles.td, { flex: 4 }]}>{ln.description}</Text>
                <Text style={[styles.td, { flex: 1.1, textAlign: "right" }]}>{qtyUnit}</Text>
                <Text style={[styles.td, { flex: 1, textAlign: "right", color: isOffered ? "#8C6B2F" : undefined }]}>
                  {isOffered ? "Offert" : fmtCHF(ln.unit_price)}
                </Text>
                <Text style={[styles.td, { flex: 0.6, textAlign: "right" }]}>{ln.vat_rate}%</Text>
                <Text style={[styles.td, { flex: 1.1, textAlign: "right", fontFamily: "Helvetica-Bold", color: isOffered ? "#8C6B2F" : undefined }]}>
                  {isOffered ? "Offert" : fmtCHF(total)}
                </Text>
              </View>
            );
          })}
          {/* Phase 101ea.6 — Frais additionnels du devis traiteur (livraison,
              service, minimum garanti…). Affichés en fin de tableau avec un
              fond légèrement ambré pour les distinguer des articles.
              Phase 101ea.7 — Frais offerts (amount=0) : afficher « Offert »
              au lieu de « 0.00 », comme le PDF devis. */}
          {(t.fees || []).filter((f) => {
            // Phase 101eq.2 cohérence : n'exclure que les frais vides
            // (ni description ni montant)
            const hasDesc = (f.description || "").trim().length > 0;
            const hasAmount = Number(f.amount || 0) !== 0;
            return hasDesc || hasAmount;
          }).map((f, idx) => {
            const amount = Number(f.amount || 0);
            const isOffered = amount === 0;
            return (
              <View key={f.id || `fee-${idx}`} style={[styles.tr, { backgroundColor: "#FBF4E9" }]}>
                <Text style={[styles.td, { flex: 4, fontStyle: "italic", color: "#8C6B2F" }]}>
                  {f.description || "Frais additionnel"}
                </Text>
                <Text style={[styles.td, { flex: 1.1, textAlign: "right", color: "#8C6B2F" }]}>—</Text>
                <Text style={[styles.td, { flex: 1, textAlign: "right", color: "#8C6B2F" }]}>
                  {isOffered ? "Offert" : "—"}
                </Text>
                <Text style={[styles.td, { flex: 0.6, textAlign: "right", color: "#8C6B2F" }]}>
                  {isOffered ? "" : `${Number(f.vat_rate || 0)}%`}
                </Text>
                <Text style={[styles.td, { flex: 1.1, textAlign: "right", fontFamily: "Helvetica-Bold", color: "#8C6B2F" }]}>
                  {isOffered ? "Offert" : fmtCHF(amount)}
                </Text>
              </View>
            );
          })}
        </View>

        {/* === Totaux ===
            Règle métier :
            - HT par défaut (B2B / revendeurs) : détail HT + Remise éventuelle + TVA + TTC
            - TTC (particuliers) : TOTAL TTC + (dont TVA X.XX CHF) entre parenthèses
        */}
        <View style={styles.totals}>
          {t.discountAmount > 0 && (
            <>
              <View style={styles.totalRow}>
                <Text style={styles.totalLabel}>Sous-total {t.includeVat ? "TTC" : "HT"}</Text>
                <Text style={styles.totalValue}>{fmtCHF(t.subtotal)} CHF</Text>
              </View>
              <View style={styles.totalRow}>
                <Text style={[styles.totalLabel, { color: "#C05C47" }]}>
                  Remise {t.discountPct}%{inv.discount_label ? ` (${inv.discount_label})` : ""}
                </Text>
                <Text style={[styles.totalValue, { color: "#C05C47" }]}>− {fmtCHF(t.discountAmount)} CHF</Text>
              </View>
            </>
          )}
          {/* Phase 101ea.6 — Frais additionnels dans le récap totaux */}
          {t.feesTotal > 0 && (
            <View style={styles.totalRow}>
              <Text style={[styles.totalLabel, { color: "#8C6B2F" }]}>
                Frais additionnels {t.includeVat ? "TTC" : "HT"}
              </Text>
              <Text style={[styles.totalValue, { color: "#8C6B2F" }]}>+ {fmtCHF(t.feesTotal)} CHF</Text>
            </View>
          )}
          {t.includeVat ? (
            <View style={styles.totalGrand}>
              <Text style={[styles.totalLabel, { fontSize: 11, color: "#1A1C1A", fontFamily: "Helvetica-Bold" }]}>TOTAL TTC</Text>
              <View style={{ alignItems: "flex-end" }}>
                <Text style={[styles.totalValue, { fontSize: 12, color: "#1F4E3D" }]}>{fmtCHF(t.ttc)} CHF</Text>
                {(() => {
                  // Phase 101gd (Feb 2026) — Détail multi-taux TVA (obligation
                  // légale suisse art. 26 LTVA quand > 1 taux dans la facture).
                  const vatByRate = {};
                  Object.entries(t.vatGroups || {}).forEach(([r, base]) => {
                    const rate = Number(r) || 0;
                    if (rate === 0) return;
                    const amt = base - base / (1 + rate / 100);
                    if (amt > 0.005) vatByRate[rate] = (vatByRate[rate] || 0) + amt;
                  });
                  const rates = Object.keys(vatByRate).map(Number).sort();
                  if (rates.length === 0) return null;
                  if (rates.length === 1) {
                    return (
                      <Text style={{ fontSize: 8, color: "#575A57", marginTop: 1 }}>
                        (dont TVA {rates[0]}% : {fmtCHF(vatByRate[rates[0]])} CHF)
                      </Text>
                    );
                  }
                  // Multi-taux : "dont TVA X CHF (2.6% : Y · 8.1% : Z)"
                  const breakdown = rates.map((r) => `${r}% : ${fmtCHF(vatByRate[r])}`).join(" · ");
                  return (
                    <Text style={{ fontSize: 8, color: "#575A57", marginTop: 1 }}>
                      (dont TVA {fmtCHF(t.vat)} CHF · {breakdown})
                    </Text>
                  );
                })()}
              </View>
            </View>
          ) : (
            <>
              <View style={styles.totalRow}>
                <Text style={styles.totalLabel}>Total HT</Text>
                <Text style={styles.totalValue}>{fmtCHF(t.ht)} CHF</Text>
              </View>
              {Object.entries(t.vatGroups).map(([r, base]) => (
                <View key={r} style={styles.totalRow}>
                  <Text style={styles.totalLabel}>TVA {r}%</Text>
                  <Text style={styles.totalValue}>{fmtCHF(base * Number(r) / 100)} CHF</Text>
                </View>
              ))}
              {t.roundingAdjustment !== 0 && (
                <View style={styles.totalRow}>
                  <Text style={[styles.totalLabel, { color: "#8C6B2F" }]}>Arrondi 0.05 (CH)</Text>
                  <Text style={[styles.totalValue, { color: "#8C6B2F" }]}>
                    {t.roundingAdjustment > 0 ? "+" : ""}{fmtCHF(t.roundingAdjustment)} CHF
                  </Text>
                </View>
              )}
              <View style={styles.totalGrand}>
                <Text style={[styles.totalLabel, { fontSize: 11, color: "#1A1C1A", fontFamily: "Helvetica-Bold" }]}>TOTAL TTC</Text>
                <Text style={[styles.totalValue, { fontSize: 12, color: "#1F4E3D" }]}>{fmtCHF(t.ttc)} CHF</Text>
              </View>
            </>
          )}
          {(inv?.credit_applications || []).map((c) => (
            <View key={c.payment_id} style={styles.totalRow}>
              <Text style={styles.totalLabel}>Avoir déduit {c.credit_note_reference}</Text>
              <Text style={styles.totalValue}>-{fmtCHF(c.amount)} CHF</Text>
            </View>
          ))}
          {paid > 0 && (
            <View style={styles.paidRow}>
              <Text style={styles.paidText}>
                {paid - credit > 0.004 ? `Payé : ${fmtCHF(paid - credit)} CHF · ` : ""}Reste dû : <Text style={{ fontFamily: "Helvetica-Bold" }}>{fmtCHF(remaining)} CHF</Text>
              </Text>
            </View>
          )}
        </View>

        {inv.notes ? <Text style={{ marginTop: 10, fontSize: 9, color: "#575A57", lineHeight: 1.4 }}>{inv.notes}</Text> : null}

        <Text style={styles.footer}>
          Facture {inv.reference} générée par {COMPANY_HEADER} le {fmtDate(inv.issue_date)}
        </Text>
      </Page>

      {/* === Phase 101bz (18/02/2026) — QR-Bill sur page dédiée (toujours page 2) ===
          Avant : le QR-Bill était rendu à la suite du contenu de la facture. Si la
          facture était courte, react-pdf laissait un espace vide entre le contenu et
          le QR-Bill, ou pire, générait une page blanche parasite. Solution : forcer
          une nouvelle page A4 réservée au QR-Bill, systématiquement. Comportement
          conforme aux pratiques bancaires suisses (paiement détachable). */}
      <Page size="A4" style={styles.page}>
        <Text style={styles.qrPageHint}>
          Bulletin de paiement — à joindre au règlement (partie détachable).
        </Text>
        <View style={styles.qrBillBoxPage2}>
          {qrPngDataUrl ? (
            <Image src={qrPngDataUrl} style={styles.qrBillImage} />
          ) : (
            <Text style={styles.qrBillMissing}>
              QR-Bill non généré — vérifiez que l&apos;IBAN et l&apos;adresse créancier (rue + NPA + ville) sont renseignés dans Paramètres.
            </Text>
          )}
        </View>
        <Text style={styles.footer}>
          Facture {inv.reference} · Bulletin de paiement · {COMPANY_HEADER}
        </Text>
      </Page>
    </Document>
  );
}

export default InvoicePdf;
